import React, { useEffect, useState } from 'react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, deleteUser, EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } from 'firebase/auth';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ShieldIcon, AlertTriangleIcon, LoaderIcon, LockIcon, MailIcon, KeyIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
export const StudentAccountSettings = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  // Email Edit State
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailPendingMessage, setEmailPendingMessage] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  // Password Edit State
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  // Delete Account State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthMode, setReauthMode] = useState<'delete' | 'email'>('delete');
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReauthorizingEmail, setIsReauthorizingEmail] = useState(false);
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEmail(data.email || currentUser.email || '');
        } else {
          setEmail(currentUser.email || '');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load account data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [currentUser]);
  // --- Email change handlers ---
  const handleStartEditEmail = () => {
    setIsEditingEmail(true);
    setNewEmail('');
    setEmailError('');
    setEmailPendingMessage('');
  };
  const handleCancelEmail = () => {
    setIsEditingEmail(false);
    setNewEmail('');
    setEmailError('');
  };
  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };
  const handleSaveEmail = () => {
    setEmailError('');
    const trimmed = newEmail.trim();
    if (!trimmed) {
      setEmailError('Please enter a new email address');
      return;
    }
    if (!validateEmail(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (trimmed.toLowerCase() === email.toLowerCase()) {
      setEmailError('This is already your email address');
      return;
    }
    // Open re-auth modal in email mode
    setReauthMode('email');
    setReauthPassword('');
    setReauthError('');
    setShowReauthModal(true);
  };
  const handleReauthAndUpdateEmail = async () => {
    if (!currentUser || !currentUser.email) return;
    if (!reauthPassword) {
      setReauthError('Please enter your password');
      return;
    }
    setIsReauthorizingEmail(true);
    setReauthError('');
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, reauthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await verifyBeforeUpdateEmail(currentUser, newEmail.trim());
      toast.success('Verification email sent');
      setEmailPendingMessage(`We sent a verification link to ${newEmail.trim()}. Your email will update after you confirm it.`);
      setShowReauthModal(false);
      setIsEditingEmail(false);
      setNewEmail('');
      setReauthPassword('');
    } catch (error: any) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setReauthError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        setReauthError('That email is already in use by another account.');
      } else if (error.code === 'auth/invalid-email') {
        setReauthError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setReauthError('Too many attempts. Please wait a moment and try again.');
      } else {
        setReauthError('Something went wrong. Please try again.');
      }
    } finally {
      setIsReauthorizingEmail(false);
    }
  };
  // --- Password change handlers ---
  const handleStartEditPassword = () => {
    setIsEditingPassword(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };
  const handleCancelPassword = () => {
    setIsEditingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowCurrentPwd(false);
    setShowNewPwd(false);
    setShowConfirmPwd(false);
  };
  const handleSavePassword = async () => {
    if (!currentUser || !currentUser.email) return;
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    setIsSavingPassword(true);
    try {
      // Re-authenticate inline using current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      toast.success('Password updated successfully');
      handleCancelPassword();
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordError('Your current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak. Try a longer one.');
      } else if (error.code === 'auth/too-many-requests') {
        setPasswordError('Too many attempts. Please wait a moment and try again.');
      } else {
        setPasswordError('Failed to update password. Please try again.');
      }
    } finally {
      setIsSavingPassword(false);
    }
  };
  // --- Delete handlers ---
  const handleDeleteConfirmed = () => {
    setShowDeleteModal(false);
    setReauthMode('delete');
    setReauthPassword('');
    setReauthError('');
    setShowReauthModal(true);
  };
  const handleCancelReauth = () => {
    setShowReauthModal(false);
    setReauthPassword('');
    setReauthError('');
  };
  const handleReauthAndDelete = async () => {
    if (!currentUser || !currentUser.email) return;
    if (!reauthPassword) {
      setReauthError('Please enter your password');
      return;
    }
    setIsDeleting(true);
    setReauthError('');
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, reauthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      const userDocRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userDocRef);
      await deleteUser(currentUser);
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: any) {
      console.error('Error during account deletion:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setReauthError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setReauthError('Too many attempts. Please wait a moment and try again.');
      } else {
        setReauthError('Something went wrong. Please try again.');
      }
      setIsDeleting(false);
    }
  };
  // --- Styles ---
  const cardClass = `rounded-xl p-6 shadow-sm border transition-all ${isDark ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`;
  const dangerCardClass = `rounded-xl p-6 shadow-sm border transition-all ${isDark ? 'bg-navy-800 border-red-500/20' : 'bg-white border-red-200'}`;
  const rowLabelClass = `text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const rowValueClass = `text-base ${isDark ? 'text-white' : 'text-gray-900'}`;
  const rowSubtitleClass = `text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const fieldLabelClass = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const inputClass = `w-full px-4 py-2.5 rounded-lg border transition-colors focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none ${isDark ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const primaryBtnClass = `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed`;
  const secondaryBtnClass = `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-gray-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`;
  const ghostBtnClass = `inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`;
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-7103">
        <LoaderIcon className={`w-8 h-8 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-500'}`} data-id="element-7104" />
      </div>;
  }
  // Helper: password input with show/hide toggle
  const PasswordInput = ({
    value,
    onChange,
    placeholder,
    show,
    onToggleShow,
    autoFocus,
    disabled
  }: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    show: boolean;
    onToggleShow: () => void;
    autoFocus?: boolean;
    disabled?: boolean;
  }) => <div className="relative" data-id="element-7105">
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} className={`${inputClass} pr-10`} placeholder={placeholder} autoFocus={autoFocus} disabled={disabled} autoComplete="new-password" data-id="element-7106" />
      <button type="button" onClick={onToggleShow} tabIndex={-1} aria-label={show ? 'Hide password' : 'Show password'} className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7107">
        {show ? <EyeOffIcon className="w-4 h-4" data-id="element-7108" /> : <EyeIcon className="w-4 h-4" data-id="element-7109" />}
      </button>
    </div>;
  return <div className="min-h-screen p-6 md:p-8" data-id="element-7110">
      <div className="max-w-[900px] mx-auto space-y-8" data-id="element-7111">
        {/* Page Header */}
        <div data-id="element-7112">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7113">
            Account Settings
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7114">
            Manage your account security and preferences.
          </p>
        </div>

        {/* Section 1: Security */}
        <div className={cardClass} data-id="element-7115">
          <div className="flex items-center gap-3 mb-2" data-id="element-7116">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-blue-300' : 'bg-blue-50 text-blue-600'}`} data-id="element-7117">
              <ShieldIcon className="w-5 h-5" data-id="element-7118" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7119">
              Security
            </h2>
          </div>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7120">
            Update your sign-in email and password.
          </p>

          {/* --- Email Row --- */}
          <div className={`py-5 ${isDark ? 'border-t border-navy-700' : 'border-t border-gray-100'}`} data-id="element-7121">
            <div className="flex items-start justify-between gap-4" data-id="element-7122">
              <div className="min-w-0 flex-1" data-id="element-7123">
                <p className={rowLabelClass} data-id="element-7124">Email Address</p>
                <p className={`${rowValueClass} mt-1 break-all`} data-id="element-7125">{email}</p>
                {emailPendingMessage && !isEditingEmail && <div className={`mt-3 flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-100'}`} data-id="element-7126">
                    <MailIcon className="w-4 h-4 mt-0.5 shrink-0" data-id="element-7127" />
                    <span data-id="element-7128">{emailPendingMessage}</span>
                  </div>}
              </div>
              {!isEditingEmail && <button onClick={handleStartEditEmail} className={secondaryBtnClass} data-id="element-7129">
                  Change
                </button>}
            </div>

            {isEditingEmail && <div className="mt-5 space-y-4" data-id="element-7130">
                <div data-id="element-7131">
                  <label className={fieldLabelClass} data-id="element-7132">New email address</label>
                  <input type="email" value={newEmail} onChange={e => {
                setNewEmail(e.target.value);
                if (emailError) setEmailError('');
              }} onKeyDown={e => {
                if (e.key === 'Enter' && !isSavingEmail) {
                  handleSaveEmail();
                }
              }} className={inputClass} placeholder="you@example.com" autoFocus autoComplete="email" data-id="element-7133" />
                  {emailError && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-7134">
                      {emailError}
                    </p>}
                </div>
                <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7135">
                  <MailIcon className="w-4 h-4 mt-0.5 shrink-0" data-id="element-7136" />
                  <span data-id="element-7137">
                    We'll send a verification link to your new email. Your email
                    won't change until you confirm it.
                  </span>
                </div>
                <div className="flex items-center justify-end gap-3 pt-1" data-id="element-7138">
                  <button onClick={handleCancelEmail} disabled={isSavingEmail} className={ghostBtnClass} data-id="element-7139">
                    Cancel
                  </button>
                  <button onClick={handleSaveEmail} disabled={isSavingEmail || !newEmail.trim()} className={primaryBtnClass} data-id="element-7140">
                    Save changes
                  </button>
                </div>
              </div>}
          </div>

          {/* --- Password Row --- */}
          <div className={`py-5 ${isDark ? 'border-t border-navy-700' : 'border-t border-gray-100'}`} data-id="element-7141">
            <div className="flex items-start justify-between gap-4" data-id="element-7142">
              <div className="min-w-0 flex-1" data-id="element-7143">
                <p className={rowLabelClass} data-id="element-7144">Password</p>
                <p className={`${rowSubtitleClass} mt-1`} data-id="element-7145">
                  Use a strong password to keep your account secure.
                </p>
              </div>
              {!isEditingPassword && <button onClick={handleStartEditPassword} className={secondaryBtnClass} data-id="element-7146">
                  Change
                </button>}
            </div>

            {isEditingPassword && <div className="mt-5 space-y-4" data-id="element-7147">
                <div data-id="element-7148">
                  <label className={fieldLabelClass} data-id="element-7149">Current password</label>
                  <PasswordInput value={currentPassword} onChange={setCurrentPassword} placeholder="Enter your current password" show={showCurrentPwd} onToggleShow={() => setShowCurrentPwd(s => !s)} autoFocus disabled={isSavingPassword} data-id="element-7150" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7151">
                  <div data-id="element-7152">
                    <label className={fieldLabelClass} data-id="element-7153">New password</label>
                    <PasswordInput value={newPassword} onChange={setNewPassword} placeholder="At least 8 characters" show={showNewPwd} onToggleShow={() => setShowNewPwd(s => !s)} disabled={isSavingPassword} data-id="element-7154" />
                  </div>
                  <div data-id="element-7155">
                    <label className={fieldLabelClass} data-id="element-7156">
                      Confirm new password
                    </label>
                    <PasswordInput value={confirmPassword} onChange={setConfirmPassword} placeholder="Re-enter new password" show={showConfirmPwd} onToggleShow={() => setShowConfirmPwd(s => !s)} disabled={isSavingPassword} data-id="element-7157" />
                  </div>
                </div>
                {passwordError && <p className="text-sm text-red-500 font-medium" data-id="element-7158">
                    {passwordError}
                  </p>}
                <div className="flex items-center justify-end gap-3 pt-1" data-id="element-7159">
                  <button onClick={handleCancelPassword} disabled={isSavingPassword} className={ghostBtnClass} data-id="element-7160">
                    Cancel
                  </button>
                  <button onClick={handleSavePassword} disabled={isSavingPassword || !currentPassword || !newPassword || !confirmPassword} className={primaryBtnClass} data-id="element-7161">
                    {isSavingPassword && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-7162" />}
                    {isSavingPassword ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              </div>}
          </div>
        </div>

        {/* Section 2: Danger Zone */}
        <div className={dangerCardClass} data-id="element-7163">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-red-200 dark:border-red-500/20" data-id="element-7164">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`} data-id="element-7165">
              <AlertTriangleIcon className="w-5 h-5" data-id="element-7166" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`} data-id="element-7167">
              Danger Zone
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7168">
            <div data-id="element-7169">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7170">
                Permanently delete your account and all associated data.
              </p>
            </div>
            <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors whitespace-nowrap" data-id="element-7171">
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-7172">
          <div className={`w-full max-w-md rounded-xl p-6 shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-7173">
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7174">
              Delete Account
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7175">
              Are you sure you want to delete your account? This action cannot
              be undone.
            </p>
            <div className="flex items-center justify-end gap-3" data-id="element-7176">
              <button onClick={() => setShowDeleteModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-7177">
                Cancel
              </button>
              <button onClick={handleDeleteConfirmed} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors" data-id="element-7178">
                Delete Account
              </button>
            </div>
          </div>
        </div>}

      {/* Re-authentication Modal (shared: delete + email change) */}
      {showReauthModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-7179">
          <div className={`w-full max-w-md rounded-xl p-6 shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-7180">
            <div className="flex items-center gap-3 mb-4" data-id="element-7181">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-amber-300' : 'bg-amber-100 text-amber-700'}`} data-id="element-7182">
                <LockIcon className="w-5 h-5" data-id="element-7183" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7184">
                Confirm Your Identity
              </h3>
            </div>
            <p className={`mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7185">
              {reauthMode === 'email' ? 'For security, please re-enter your password to update your email address.' : 'For security, please re-enter your password to confirm account deletion.'}
            </p>

            <div className="mb-4" data-id="element-7186">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7187">
                Password
              </label>
              <input type="password" value={reauthPassword} onChange={e => {
            setReauthPassword(e.target.value);
            setReauthError('');
          }} onKeyDown={e => {
            if (e.key === 'Enter') {
              if (reauthMode === 'email' && !isReauthorizingEmail) {
                handleReauthAndUpdateEmail();
              } else if (reauthMode === 'delete' && !isDeleting) {
                handleReauthAndDelete();
              }
            }
          }} className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none ${isDark ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`} placeholder="Enter your password" autoFocus disabled={isDeleting || isReauthorizingEmail} autoComplete="current-password" data-id="element-7188" />
              {reauthError && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-7189">
                  {reauthError}
                </p>}
            </div>

            <div className="flex items-center justify-end gap-3" data-id="element-7190">
              <button onClick={handleCancelReauth} disabled={isDeleting || isReauthorizingEmail} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`} data-id="element-7191">
                Cancel
              </button>
              {reauthMode === 'email' ? <button onClick={handleReauthAndUpdateEmail} disabled={isReauthorizingEmail || !reauthPassword} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-70" data-id="element-7192">
                  {isReauthorizingEmail ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-7193" /> : null}
                  {isReauthorizingEmail ? 'Sending...' : 'Confirm & Send'}
                </button> : <button onClick={handleReauthAndDelete} disabled={isDeleting || !reauthPassword} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-70" data-id="element-7194">
                  {isDeleting ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-7195" /> : null}
                  {isDeleting ? 'Deleting...' : 'Confirm & Delete'}
                </button>}
            </div>
          </div>
        </div>}
    </div>;
};